import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Broadcast, YoutubeLogo, FacebookLogo, CheckCircle, Warning, Copy } from "@phosphor-icons/react";

const PROVIDER_META = {
  youtube: { label: "YouTube Live", Icon: YoutubeLogo, color: "#FF0000",
    help: "Google Cloud → Credentials → OAuth client (Web). Enable YouTube Data API v3 + Live Streaming API." },
  facebook: { label: "Facebook Live", Icon: FacebookLogo, color: "#1877F2",
    help: "Meta app → Facebook Login. Needs publish_video + Pages permissions (App Review for public use)." },
};

export default function AdminLiveConnect() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data: d } = await api.get("/broadcast/oauth/config"); setData(d); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      ["youtube_client_id", "youtube_client_secret", "facebook_client_id", "facebook_client_secret"].forEach((k) => {
        if (form[k] !== undefined && form[k] !== "") patch[k] = form[k];
      });
      if (!Object.keys(patch).length) { toast.info("Enter a value to save."); setBusy(false); return; }
      await api.post("/broadcast/oauth/config", patch);
      toast.success("Saved — Live Connect is ready. Broadcasters can now connect these platforms.");
      setForm({});
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Redirect URI copied"); };

  if (!data) return <div className="py-16 text-center text-muted-foreground" data-testid="liveconnect-loading">Loading…</div>;

  const inputCls = "w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary";

  return (
    <div className="space-y-6" data-testid="admin-live-connect">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Broadcast size={22} weight="fill" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Live Connect</h1>
          <p className="text-sm text-muted-foreground">Add your YouTube & Facebook OAuth app credentials so broadcasters can connect in one click. Secrets are encrypted at rest.</p>
        </div>
      </div>

      {["youtube", "facebook"].map((prov) => {
        const meta = PROVIDER_META[prov];
        const p = data.providers[prov] || {};
        const idKey = `${prov}_client_id`, secKey = `${prov}_client_secret`;
        return (
          <div key={prov} className="rounded-2xl border border-border bg-card p-5" data-testid={`liveconnect-${prov}`}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <meta.Icon size={26} weight="fill" style={{ color: meta.color }} />
                <div>
                  <h2 className="text-lg font-semibold">{meta.label}</h2>
                  <p className="text-xs text-muted-foreground">{meta.help}</p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${p.client_id_set && p.client_secret_set ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>
                {p.client_id_set && p.client_secret_set ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                {p.client_id_set && p.client_secret_set ? "Configured" : "Not configured"}
              </span>
            </div>

            <div className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2">
              <span className="text-xs font-semibold text-muted-foreground">Redirect URI:</span>
              <code className="flex-1 truncate font-mono-metric text-xs">{p.redirect_uri}</code>
              <button data-testid={`copy-redirect-${prov}`} onClick={() => copy(p.redirect_uri)} className="rounded-full border border-border p-1.5 hover:border-primary"><Copy size={13} /></button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client / App ID {p.client_id_set && <span className="text-emerald-500">· set ({p.client_id_preview})</span>}</span>
                <input data-testid={`input-${idKey}`} value={form[idKey] ?? ""} onChange={(e) => setForm({ ...form, [idKey]: e.target.value })}
                  placeholder={p.client_id_set ? "•••• (leave blank to keep)" : "Paste client / app ID"} className={`mt-1 ${inputCls}`} />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client / App Secret {p.client_secret_set && <span className="text-emerald-500">· set</span>}</span>
                <input data-testid={`input-${secKey}`} type="password" value={form[secKey] ?? ""} onChange={(e) => setForm({ ...form, [secKey]: e.target.value })}
                  placeholder={p.client_secret_set ? "•••• (leave blank to keep)" : "Paste secret"} className={`mt-1 ${inputCls}`} />
              </label>
            </div>
          </div>
        );
      })}

      <button data-testid="liveconnect-save" onClick={save} disabled={busy}
        className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
        {busy ? "Saving…" : "Save credentials"}
      </button>
      <p className="text-xs text-muted-foreground">After saving, broadcasters connect from <span className="font-semibold">Meet → Live Broadcasting</span>. Blank fields keep the current value.</p>
    </div>
  );
}
